import { useState } from 'react';
import { FiTarget, FiAlertCircle, FiCheckCircle } from 'react-icons/fi';
import { fmtCurrency } from '../../../utils/formatNumber';
import styles from '../Reports.module.css';

export default function IncomeExpenditureReport({ data }) {
  const [showTarget, setShowTarget] = useState(false);
  const [targetPct, setTargetPct] = useState(85);

  if (!data) return null;

  const { incomeRows, totalIncome, expenditureRows, totalExpenditure, netIncome } = data;
  const isProfit = netIncome >= 0;

  const spendingPct      = totalIncome > 0 ? (totalExpenditure / totalIncome) * 100 : 0;
  const targetAmount     = totalIncome * (targetPct / 100);
  const remainingToSpend = targetAmount - totalExpenditure;
  const isTargetMet      = totalExpenditure >= targetAmount && totalIncome > 0;
  const progressPct      = targetAmount > 0 ? Math.min((totalExpenditure / targetAmount) * 100, 100) : 0;

  const handleTargetPct = (e) => {
    const v = parseInt(e.target.value, 10);
    if (Number.isFinite(v)) setTargetPct(Math.min(100, Math.max(1, v)));
  };

  return (
    <div className={styles.ieWrap}>

      {/* ── Spending Target Panel ── */}
      <div className={`card ${styles.targetCard}`}>
        <div className={styles.targetHeader}>
          <div className={styles.targetTitleGroup}>
            <FiTarget size={15} style={{ flexShrink: 0, color: 'var(--color-primary)' }} />
            <span className={styles.targetTitle}>Spending Target Analysis</span>
            <span className={styles.targetSubtitle}>
              Charitable trusts (Sec. 11/12) must spend a minimum % of annual income
            </span>
          </div>
          <label className={styles.ieToggle}>
            <input
              type="checkbox"
              checked={showTarget}
              onChange={(e) => setShowTarget(e.target.checked)}
            />
            <span className={styles.ieToggleTrack} data-on={showTarget}>
              <span className={styles.ieToggleThumb} />
            </span>
          </label>
        </div>

        {showTarget && (
          <div className={styles.targetBody}>
            <div className={styles.targetInputRow}>
              <label className={styles.targetInputLabel}>Required spending target</label>
              <div className={styles.pctInputWrap}>
                <input
                  type="number" min="1" max="100" step="1"
                  className={styles.pctInput}
                  value={targetPct}
                  onChange={handleTargetPct}
                />
                <span className={styles.pctSign}>%</span>
              </div>
              <span className={styles.targetInputHint}>of total income</span>
            </div>

            {totalIncome > 0 ? (
              <>
                <div className={styles.targetMetrics}>
                  <div className={styles.metric}>
                    <span className={styles.metricLabel}>Total Income</span>
                    <span className={styles.metricValue}>{fmtCurrency(totalIncome)}</span>
                  </div>
                  <div className={styles.metric}>
                    <span className={styles.metricLabel}>Target Spend ({targetPct}%)</span>
                    <span className={styles.metricValue}>{fmtCurrency(targetAmount)}</span>
                  </div>
                  <div className={styles.metric}>
                    <span className={styles.metricLabel}>Spent So Far</span>
                    <span className={styles.metricValue}>{fmtCurrency(totalExpenditure)}</span>
                  </div>
                  <div className={`${styles.metric} ${isTargetMet ? styles.metricOk : styles.metricGap}`}>
                    <span className={styles.metricLabel}>
                      {isTargetMet ? 'Surplus Over Target' : 'Still to Spend'}
                    </span>
                    <span className={`${styles.metricValue} ${isTargetMet ? styles.textOk : styles.textWarn}`}>
                      {fmtCurrency(Math.abs(remainingToSpend))}
                    </span>
                  </div>
                </div>

                <div className={styles.progressSection}>
                  <div className={styles.progressBar}>
                    <div
                      className={`${styles.progressFill} ${isTargetMet ? styles.progressFillOk : styles.progressFillWarn}`}
                      style={{ width: `${progressPct}%` }}
                    />
                  </div>
                  <div className={styles.progressLabels}>
                    <span className={styles.progressPct}>{spendingPct.toFixed(1)}% spent</span>
                    <span className={isTargetMet ? styles.textOk : styles.textWarn}>
                      {isTargetMet
                        ? <><FiCheckCircle size={12} /> Target met!</>
                        : <><FiAlertCircle size={12} /> {(targetPct - spendingPct).toFixed(1)}% more spending needed</>}
                    </span>
                  </div>
                </div>
              </>
            ) : (
              <p className={styles.noIncomeHint}>No income recorded for this period — run the report first.</p>
            )}
          </div>
        )}
      </div>

      {/* ── Two-column I&E layout ── */}
      <div className={styles.twoColGrid}>

        {/* Expenditure — left */}
        <div className="card">
          <div className="card-header"><h3>Expenditure</h3></div>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Account</th>
                  <th className={styles.amtRight}>Amount (₹)</th>
                  <th className={styles.amtRight}>% of Income</th>
                </tr>
              </thead>
              <tbody>
                {expenditureRows?.length === 0 && (
                  <tr><td colSpan={3} style={{ color: 'var(--color-text-muted)' }}>No expenditure accounts</td></tr>
                )}
                {expenditureRows?.map((r, i) => {
                  const pct = totalIncome > 0 ? (r.balance / totalIncome) * 100 : null;
                  return (
                    <tr key={i}>
                      <td>{r.accountName}</td>
                      <td className={styles.amtRight}>{fmtCurrency(r.balance)}</td>
                      <td className={`${styles.amtRight} ${styles.pctCell}`}>
                        {pct !== null ? `${pct.toFixed(1)}%` : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td><strong>Total Expenditure</strong></td>
                  <td className={styles.amtRight}><strong>{fmtCurrency(totalExpenditure)}</strong></td>
                  <td className={`${styles.amtRight} ${styles.pctCell}`}>
                    <strong>{totalIncome > 0 ? `${spendingPct.toFixed(1)}%` : '—'}</strong>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* Income — right */}
        <div className="card">
          <div className="card-header"><h3>Income</h3></div>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Account</th>
                  <th className={styles.amtRight}>Amount (₹)</th>
                </tr>
              </thead>
              <tbody>
                {incomeRows?.length === 0 && (
                  <tr><td colSpan={2} style={{ color: 'var(--color-text-muted)' }}>No income accounts</td></tr>
                )}
                {incomeRows?.map((r, i) => (
                  <tr key={i}>
                    <td>{r.accountName}</td>
                    <td className={styles.amtRight}>{fmtCurrency(r.balance)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td><strong>Total Income</strong></td>
                  <td className={styles.amtRight}><strong>{fmtCurrency(totalIncome)}</strong></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>

      {/* ── Net summary ── */}
      <div className="card">
        <div className="table-container">
          <table>
            <tbody>
              <tr>
                <td>Total Income</td>
                <td className={styles.amtRight}>{fmtCurrency(totalIncome)}</td>
                <td className={`${styles.amtRight} ${styles.pctCell}`} />
              </tr>
              <tr>
                <td>Total Expenditure</td>
                <td className={styles.amtRight}>({fmtCurrency(totalExpenditure)})</td>
                <td className={`${styles.amtRight} ${styles.pctCell}`}>
                  {totalIncome > 0 ? `${spendingPct.toFixed(1)}% of income` : ''}
                </td>
              </tr>
              <tr>
                <td><strong>{isProfit ? 'Net Income (Surplus)' : 'Net Loss (Deficit)'}</strong></td>
                <td className={`${styles.amtRight} ${isProfit ? styles.balanceOk : styles.balanceDiff}`}>
                  <strong>{fmtCurrency(Math.abs(netIncome))}</strong>
                </td>
                <td className={`${styles.amtRight} ${styles.pctCell}`} />
              </tr>
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
}
